import { Search, Flex, Text, Menu, ToolIcon, Badge } from "@theorgchartdev/storybook";
import React, { useEffect } from "react";
import cx from "classnames";
import { SwitchWrapper } from "@/appComponents/Blocks/Menus/FieldToggleMenu/FieldToggleMenu";
import * as stateLib from "@/Utils/StateLib";
import { ConflictDetectionAlert } from "@/appComponents/Blocks/Composites/ConflictDetectionAlert/ConflictDetectionAlert";
import { appstr } from "@coreapp/utils/appstr";
import { QActivityReportRFC } from "@coreapp/CoreApp/QActivityReportRFC";
import { Globals } from "@coreapp/CoreApp/Globals";
import { appstrp } from "@coreapp/utils/appstrp";
import { enumOverrideType } from "@coreapp/constants";

const GetToggleOptionContent = (option, checked) => {
  const baseClass = "ocn-field-toggle-option";
  let checkedClass = checked && `${baseClass}-label--checked`;
  return (
    <span className={baseClass}>
      <span className={cx(`${baseClass}-label`, checkedClass)}>
        <Text size={"small"}>{appstr(option.value)}</Text>
      </span>
      <span className={`${baseClass}-switch`}>
        <SwitchWrapper
          id={"activity-filter-switch-" + option.value}
          checked={checked}
          option={option}
          onChange={e => option.clickHandler(e.target.value)}
        />
      </span>
    </span>
  );
};

const BuildFilterOptions = (onChange, activeFilters) => {
  let filterOptions = [];
  let types = Object.values(enumOverrideType).filter(type => type !== "Lock");
  for (let i in types) {
    let option = {};
    option.value = types[i];
    option.clickHandler = o => onChange(o.value);
    let checked = activeFilters.includes(option.value);
    option.content = GetToggleOptionContent(option, checked);
    filterOptions.push(option);
  }

  let valueMismatch = {};
  valueMismatch.value = "Value Mismatch";
  valueMismatch.divider = true;
  let valueMismatchChecked = activeFilters.includes(valueMismatch.value);
  valueMismatch.clickHandler = o => onChange(o.value);
  valueMismatch.content = GetToggleOptionContent(valueMismatch, valueMismatchChecked);
  filterOptions.push(valueMismatch);

  let showConflictsOnly = {};
  showConflictsOnly.value = "Show conflicts only";
  let conflictOnlyChecked = activeFilters.includes(showConflictsOnly.value);
  showConflictsOnly.clickHandler = o => onChange(o.value);
  showConflictsOnly.content = GetToggleOptionContent(showConflictsOnly, conflictOnlyChecked);
  filterOptions.push(showConflictsOnly);

  return filterOptions;
};

export const ActivityHeader = ({
  onInput,
  onResetInput,
  onFilterChange,
  overridesShownList,
  unfilteredOverrideList,
  searchKey = "",
  activeFilters = [],
  searchRef
}) => {
  const shownCount = Object.values(overridesShownList).reduce((sum, list) => sum + list.length, 0);
  const unfilteredCount = unfilteredOverrideList.length;
  const showFeedbackFilterText =
    unfilteredCount > 0 && (activeFilters.length > 0 || searchKey.length > 0);

  // Sync searchRef value with searchKey prop on mount
  useEffect(() => {
    if (searchRef?.current) {
      searchRef.current.setValue(searchKey);
    }
  }, []);

  const filterCountString =
    shownCount > 0
      ? appstrp("Showing", " ") +
        shownCount +
        " " +
        appstrp("of", " ") +
        unfilteredCount +
        " " +
        appstrp("Changes", ".").toLowerCase()
      : appstrp("No results found", ".");

  const filterCount = activeFilters.length ? activeFilters.length.toString() : "0";

  const filterTrigger = (
    <Badge
      border
      badgeContent={filterCount}
      color="warning"
      maxLength={3}
      overlap="circular"
      pin="top-right"
      showBadge={activeFilters.length > 0}
      size={"medium"}
    >
      <ToolIcon size={"medium-small"} name={"filter"} />
    </Badge>
  );

  const wsMgr = stateLib.GetWSMgr();
  const uInfo = wsMgr.GetUserInfo();

  const downloadChanges = () => {
    var accountName = wsMgr.GetAccountName();
    var username = wsMgr.GetUserName();
    const planMgr = wsMgr.GetPlanMgr();
    var planID = planMgr.GetPlanID();
    var reportRFC = new QActivityReportRFC();
    reportRFC.InitActivityReportRFC(wsMgr);
    const requestData = {
      planID: planID,
      language: localStorage.language
    };
    reportRFC.SetParams(accountName, username, JSON.stringify(requestData));
    Globals._rfcMgr.QueueRFC(reportRFC);
  };

  return (
    <Flex width={"100%"} direction={"column"} gap={"16px"} padding={"16px 0 0 0"}>
      <ConflictDetectionAlert showButton />
      <Flex width={"100%"} justifyContent={"space-between"} gap={"8px"} alignItems={"center"}>
        <Flex width={"85%"}>
          <Search
            onInput={onInput}
            onResetInput={onResetInput}
            placeholder={appstrp("Find changes", "...")}
            timeout={0}
            size="small"
            inputProps={{ disabled: unfilteredCount === 0 }}
            handleRef={searchRef}
          />
        </Flex>
        <Flex flex={"1"} gap={"8px"} alignItems={"center"}>
          <Menu
            listHeader={{ label: appstr("Filter out by type") }}
            options={BuildFilterOptions(onFilterChange, activeFilters)}
            trigger={filterTrigger}
            listWidth={"medium"}
            position={"bottom-end"}
          />
          <ToolIcon
            dataTestId={"export-plan-changes-btn"}
            size={"medium-small"}
            name={"file-export"}
            onClick={downloadChanges}
            disabled={!(Globals._uiConfig.fCanExportExcel && uInfo.CanExportExcel())}
          />
        </Flex>
      </Flex>
      {showFeedbackFilterText && (
        <Text colorModifier={"lightgrey"} size={"xsmall"}>
          {filterCountString}
        </Text>
      )}
    </Flex>
  );
};
